import React, { useState, useEffect } from 'react';

import Button from 'b2b-ui-components-lib/lib/components/button';
import {
  faOnDedicatedSummaryPageCancel,
  faOnDedicatedSummaryCancelOrderPopupLoad,
  faOnDedicatedSummaryCancelOrderPopupSubmit,
  faOnDedicatedSummaryPageSubmit
} from 'dedicated/services/analytics.service';
import { GENERIC_ERROR_MESSAGE, ORDER_TYPE, ROUTES } from 'common/constants/app.constants';
import { NOTIFICATION_MESSAGE } from 'home/view/manage-feasibility/constants/multiSite-constants';
import { isOrderConfirmationUrl } from 'dedicated/services/summary.service';
import CancelOrderPopup from 'dedicated/components/cancelOrderPopup/cancelOrderPopup';
import EmailPopup from 'dedicated/components/emailPopup/emailPopup';
import { getCurrentLead, getCurrentOrder, saveOrder } from 'tokenGeneration/lead.util';
import { fetchOrder as fetchOrderAsync, submitOrderForSummary, updateOrderForSummary } from 'common/services/http.service';
import { isOrderSubmittedByCustomer, numberWithCommas, showServiceError, trackAnalytics } from 'common/utils/app.utils';
import WidgetComponent from './WidgetComponent';
import moment from 'moment';

const FooterMultisiteComponent = (props) => {
  const { history } = props;
  let {
    setNotification,
    cpmAccordionMeta,
    cpmFormSchema,
    selectedOption,
    appointmentData,
    setAppointmentData,
    siteReadyByDate,
    setSiteReadyByDate,
    showWidget,
    setShowWidget,
    appointmentId,
    setSelectedAppointmentId,
    tableData,
    selectedLineItem,
    setSlotBooked,
    isOrderConfirmation,
    setIsOrderConfirmation
  } = props;

  const lead = getCurrentLead?.();
  const isMultiSite = lead?.journeyType === 'MULTI_SITE' || tableData?.length > 0;

  const [showCancelPopup, setShowCancelPopup] = useState(false);
  const [viewMore, setViewMore] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showEmailPopup, setShowEmailPopup] = useState(false);

  const order = getCurrentOrder?.();

  const lineItemCount = order?.lead?.lineItemCount;
  const customer = order?.lead?.customer;
  const orderStatus = order?.orderStatus;
  const billingLevel = order?.billingType;
  const ocLinkSentAtList = order?.ocLinkSentAtList || [];
  const planDetail = order?.lead?.planDetail;
  const ocLinkSentAt = ocLinkSentAtList?.join(', ').toString();

  const checkOrderConfirmationStatus = ({ orderStatus }) => {
    const status = (orderStatus && [ORDER_TYPE.ORDER_CONFIRMATION, ORDER_TYPE.ORDER_CONFIRMATION_PENDING]).includes(orderStatus) || isOrderConfirmationUrl();
    setIsOrderConfirmation(status);
  };

  const fetchOrder = (cb = () => {}) => {
    setShowLoader(true);
    fetchOrderAsync().then(
      ({ data }) => {
        setShowLoader(false);
        if (data && data.success && data.result) {
          const order = data.result;
          saveOrder(order);
          redirectToThankYou({}, order);
          checkOrderConfirmationStatus(order);
          cb?.();
        }
      },
      (err) => {
        setShowLoader(false);
        console.error(err);
        if (isOrderConfirmation) {
          history.push(ROUTES.B2B_FAILURE);
        }
      }
    );
  };

  const redirectToThankYou = (submitOrderRes = {}, order, isSubmitted = false) => {
    const {
      lead: {
        customer: { partyId, segment },
        lineItemCount = ''
      },
      orderId,
      orderStatus
    } = order;
    if (isOrderSubmittedByCustomer(orderStatus) || isSubmitted) {
      const dateStamp = (submitOrderRes && submitOrderRes.data && submitOrderRes.data.result && submitOrderRes.data.result.submittedAt) || '';
      let redirectUrl = ROUTES.DEDICATED_THANK_YOU;
      history.push(redirectUrl, {
        leadId: orderId,
        userType: segment,
        hideNetworkSteps: true,
        via: 'SUMMARY',
        isThankYouAuth: true,
        noOfCircuits: lineItemCount,
        dateStamp
      });
    } else if (orderStatus === ORDER_TYPE.ORDER_CANCELLED) {
      let redirectUrl = ROUTES.DEDICATED_CANCEL;
      if (partyId) {
        redirectUrl = redirectUrl + '?partyId=' + partyId;
      }
      history.push(redirectUrl, { leadId: orderId, userType: segment, hideNetworkSteps: true, via: 'SUMMARY', isThankYouAuth: true, noOfCircuits: lineItemCount });
    }
  };

  const submitOrder = () => {
    setShowLoader(true);
    submitOrderForSummary().then(
      (resp) => {
        setShowLoader(false);
        redirectToThankYou(resp, order, true);
      },
      (error) => {
        console.error(error);
        setShowLoader(false);
        const errorMsg = (error && error.response && error.response.data && error.response.data.errorMsg) || GENERIC_ERROR_MESSAGE;

        showServiceError({
          show: true,
          data: errorMsg,
          redirect: false
        });
      }
    );
  };

  const getUpdateOrderRequest = () => {
    if (isMultiSite) {
      return {
        customerProjectManagerDetails: {
          name: cpmFormSchema?.name?.value,
          contactNumber: cpmFormSchema?.mobileNumber?.value,
          emailId: cpmFormSchema?.emailAddress?.value
        }
      };
    } else if (selectedOption?.id === 'Yes') {
      return {
        customerProjectManagerDetails: {
          name: cpmFormSchema?.name?.value,
          contactNumber: cpmFormSchema?.mobileNumber?.value,
          emailId: cpmFormSchema?.emailAddress?.value
        },
        siteReadiness: true,
        siteInstallAppointmentDetail: {
          appointmentId
        }
      };
    } else {
      return {
        customerProjectManagerDetails: {
          name: cpmFormSchema?.name?.value,
          contactNumber: cpmFormSchema?.mobileNumber?.value,
          emailId: cpmFormSchema?.emailAddress?.value
        },
        siteReadiness: false,
        siteReadinessDate: moment(siteReadyByDate).format('DD/MM/YYYY'),
        siteInstallAppointmentDetail: {
          appointmentId: null
        }
      };
    }
  };

  const updateOrder = () => {
    const orderRequest = getUpdateOrderRequest();
    return updateOrderForSummary(orderRequest);
  };

  const onCancel = () => {
    setShowCancelPopup(true);
    const order = getCurrentOrder();
    faOnDedicatedSummaryPageCancel(order);
  };

  const isOrderConfirmationPending = () => {
    return orderStatus === ORDER_TYPE.ORDER_CONFIRMATION_PENDING;
  };

  const onSubmit = () => {
    const params = {
      ...planDetail,
      billingType: billingLevel
    };
    const order = getCurrentOrder();
    trackAnalytics({
      eVar9: `b2b|${order?.serviceType}|buy|confirm installations|site readiness widget|edit with excel page|save`,
      prop0: `b2b|${order?.lead?.customer?.segment}|${order?.serviceType}`,
      prop15: order?.customerEmail || '', //email address
      prop17: `${order?.lead?.customer?.authorizedSignatory?.firstName || ''} ${order?.lead?.customer?.authorizedSignatory?.lastName || ''}`, //full name
      prop14: order?.lead?.customer?.authorizedSignatory?.phoneNumber || '', //phone number
      prop35: order?.companyAddress?.officeName || '',
      prop39: order?.lead?.customer?.segment || '',
      prop40: order?.serviceType || '',
      prop37: order?.orderType || '',
      // prop41=<type of site>
      // Prop43=<no. of sites>
      // prop21=<feasibility request id>
      // Prop23=<link id>
      prop9: order?.companyAddress?.pincode || '',
      prop36: order?.lead?.customer?.authorizedSignatory?.companyName || '',
      prop20: order?.companyAddress?.addressLine1 || '',
      // Prop26=<bandwidth>
      // Prop28=<site type>
      prop10: order?.orderId || '',
      // Prop29=<media type>
      // Prop22=<contract period>
      prop18: order?.lead?.planDetail?.totalPrice || '',
      prop1: order?.purchaseOrder?.poNumber || '',
      prop24: order?.documents?.PURCHASE_ORDER?.PO_DOCUMENT || '',
      prop25: order?.purchaseOrder?.purchaseOrderDate || '',
      prop2: order?.purchaseOrder?.poAmount || '',
      prop3: order?.billingType || '',
      prop4: order?.billingCycle || '',
      prop5: order?.lead?.customer?.panNumber || '', //<account number>
      prop16: order?.companyAddress?.city || '',
      // Prop7=<full name of site contact details>
      // Prop8=<mobile number of site contact details>
      // Prop9=<email address of site contact details>
      // Prop37=<customer need by date>
      prop38: order?.fiscalCode?.id || ''
      // Prop11=<full name of CPM>
      // Prop12=<mobile number of CPM>
      // Prop13=<email address of CPM>
    });
    // faOnDedicatedSummaryPageSubmit(order,params);
    if (isOrderConfirmationPending()) {
      const cb = () => {
        setShowEmailPopup(true);
      };
      fetchOrder(cb);
    } else if (
      (selectedOption?.id === 'Yes' && appointmentData.appointmentDate && appointmentData.startTime && appointmentData.endTime) ||
      siteReadyByDate ||
      (isMultiSite && tableData.length > 0)
    ) {
      updateOrder().then(
        (resp) => {
          setShowEmailPopup(true);
        },
        (error) => {
          const errorMsg = (error && error.response && error.response.data && error.response.data.errorMsg) || GENERIC_ERROR_MESSAGE;

          showServiceError({
            show: true,
            data: errorMsg,
            redirect: false
          });
        }
      );
    } else if (!isMultiSite) {
      setShowWidget(true);
    }
  };

  const onViewMoreLessClick = () => {
    setViewMore(!viewMore);
  };

  const setOTPVerification = (verified) => {
    if (verified) {
      submitOrder();
    }
  };

  const showNotificationForSentEmail = (orderStatus) => {
    if (orderStatus === ORDER_TYPE.ORDER_CONFIRMATION_PENDING) {
      const EmailSentmessage = (
        <p>
          Order confirmation link has been sent to <a href="javascript:void(0)"> {ocLinkSentAt}</a> to {NOTIFICATION_MESSAGE.CONFIRM_ORDER}
        </p>
      );
      const verifierMessage = <p>To verify the order click on {NOTIFICATION_MESSAGE.CONFIRM_ORDER}</p>;
      setNotification((prevState) => {
        return { ...prevState, show: true, message: isOrderConfirmation && isOrderConfirmationUrl() ? verifierMessage : EmailSentmessage };
      });
    }
  };

  const isSubmitDisabled = () => {
    const isCPMDetailsFilled = cpmAccordionMeta.isValidated;
    if (isMultiSite) {
      const cpmName = cpmFormSchema?.name?.value;
      const cpmMobileNumber = cpmFormSchema?.mobileNumber?.value;
      const cpmEmail = cpmFormSchema?.emailAddress?.value;

      return !cpmName || !cpmMobileNumber || !cpmEmail;
    }

    return !isCPMDetailsFilled || Object.keys(selectedOption).length === 0;
  };

  useEffect(() => {
    showNotificationForSentEmail(orderStatus);
  }, [orderStatus]);

  useEffect(() => {
    document.getElementById('root').className = 'pricing-strip';
    return () => {
      document.getElementById('root').classList.remove('pricing-strip');
    };
  }, []);

  return (
    <React.Fragment>
      <div className={`selected-plans active`}>
        {/* add class "full_plans" to show pull plan items */}
        <div className="container">
          <div className={`inside-plan ${viewMore ? 'show_full_view' : ''}`}>
            <div className="plan-info">
              <div className="plan_details_veiw">
                <div className="left_col">
                  <h4>Total Amount</h4>
                  <div class="value mt-10">
                    &#8377; {planDetail ? numberWithCommas(planDetail.totalPrice) : '-'} <span className="gst_charges">+GST Taxes</span>
                  </div>
                </div>
                <div className="right_col">
                  <div className="view_type" onClick={onViewMoreLessClick}>
                    {viewMore ? 'View Less' : 'View More'} <span className={`animatedarrow ${viewMore ? 'arrow_up' : ''} `}></span>
                  </div>
                </div>
              </div>
            </div>
            <div className="plan-details">
              <div className="item_list">
                <div className="items">
                  <div className="label">SITES IN ORDER</div>
                  <div className="value">
                    <strong>{lineItemCount}</strong>{' '}
                  </div>
                </div>
              </div>
              <div className="action">
                <Button variant="outlinebtn" onClick={onCancel}>
                  Cancel
                </Button>
                <Button className="activebtn" disabled={showLoader || isSubmitDisabled()} onClick={onSubmit}>
                  Confirm Order
                  {showLoader && <span className="loading_icon"></span>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showCancelPopup && (
        <CancelOrderPopup
          onLoad={faOnDedicatedSummaryCancelOrderPopupLoad}
          onSubmit={(data) => faOnDedicatedSummaryCancelOrderPopupSubmit(data)}
          setshowPopup={setShowCancelPopup}
          isSummaryPage={true}
          metaData={order}
        ></CancelOrderPopup>
      )}

      {showEmailPopup && (
        <EmailPopup
          setNotification={setNotification}
          customer={customer}
          setshowPopup={setShowEmailPopup}
          email={ocLinkSentAt}
          setOTPVerification={setOTPVerification}
          orderStatus={orderStatus}
          fetchOrder={fetchOrder}
          isOrderConfirmation={isOrderConfirmation && isOrderConfirmationUrl()}
        ></EmailPopup>
      )}

      {showWidget && (
        <WidgetComponent
          open={showWidget}
          setOpen={setShowWidget}
          title="Schedule Appointment"
          appointmentData={appointmentData}
          setAppointmentData={setAppointmentData}
          siteReadyByDate={siteReadyByDate}
          setSiteReadyByDate={setSiteReadyByDate}
          selectedOption={selectedOption}
          appointmentId={appointmentId}
          setSelectedAppointmentId={setSelectedAppointmentId}
          fetchOrder={fetchOrder}
          selectedLineItem={selectedLineItem}
          setSlotBooked={setSlotBooked}
        />
      )}
    </React.Fragment>
  );
};

export default FooterMultisiteComponent;
